import React, { useState, useEffect, useRef } from 'react';
import GenericCard from "@ui-components/Card/GenericCard";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { ReactComponent as IconKey } from "@assets/Icons/IconKey.svg";
import { ReactComponent as IconCopy } from "@assets/Icons/CopyIcon.svg";
import { generateToken, regenerateToken } from '../../Reflector/request/request';
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";

interface TokenResponse {
  token: string;
  expiresIn: string;
  expiresAt: string;
  createdAt: string;
  previousTokenInvalidated?: boolean;
}

const TOKEN_VALIDITY_SECONDS = 300; // 5 minutes

const TenantIdKeyAlert: React.FC = () => {
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);
  const [expiresIn, setExpiresIn] = useState<number>(TOKEN_VALIDITY_SECONDS);
  const [showToken, setShowToken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector(state => state.selection);
  
  useEffect(() => {
    if (!showToken) return;
    if (expiresIn <= 0) {
      setShowToken(false);
      setTokenData(null);
      return;
    }
    timerRef.current = setTimeout(() => setExpiresIn(e => e - 1), 1000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [expiresIn, showToken]);

  const handleGenerate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const accessToken = await getAccessTokenSilently();
      const response = await generateToken(accessToken, tenantId);
      setTokenData(response.data);
      setExpiresIn(TOKEN_VALIDITY_SECONDS);
      setShowToken(true);
    } catch (err) {
      setError('Failed to generate token. Please try again.');
      console.error('Error generating token:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!tokenData?.token) return;
    try {
      setIsLoading(true);
      setError(null);
      const accessToken = await getAccessTokenSilently();
      const response = await regenerateToken(accessToken, tenantId, { token: tokenData.token });
      setTokenData(response.data);
      setExpiresIn(TOKEN_VALIDITY_SECONDS);
    } catch (err) {
      setError('Failed to regenerate token. Please try again.');
      console.error('Error regenerating token:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (tokenData?.token) {
      navigator.clipboard.writeText(tokenData.token);
      setShowCopiedTooltip(true);
      // Hide the tooltip after 2 seconds
      setTimeout(() => setShowCopiedTooltip(false), 2000);
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  if (!showToken) {
    return (
      <div className="bg-semantic-bg-secondary border border-semantic-bg-tertiary rounded p-4 flex flex-col items-start gap-4 my-6">
        <div className="flex items-center gap-2 mb-2 md:mb-0">
          <IconKey className="w-5 h-5 text-semantic-text-secondary" />
          <span className="font-semibold text-semantic-text-secondary">Tenant ID Key</span>
        </div>
        <div className="text-semantic-text-secondary text-sm flex-1">
          Generate a unique Tenant ID Key that you can use to deploy PAYG instances. This key will be associated with your account for billing purposes and is valid for 5 minutes.
        </div>
        {error && (
          <div className="text-red-500 text-sm w-full">{error}</div>
        )}
        <div className="w-full flex-1 mt-6">
          <GenericButton
            label="Generate Tenant ID Key"
            variant="brand"
            size="md"
            iconLeftName="key"
            style={{width: "100%"}}
            onClick={handleGenerate}
            loading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-semantic-bg-secondary border border-semantic-bg-tertiary rounded p-4 flex flex-col items-start gap-4 my-6">
        <div className="flex items-center justify-between w-full mb-2">
          <div className="flex items-center gap-2">
            <IconKey className="w-5 h-5 text-semantic-text-secondary" />
            <span className="font-semibold text-semantic-text-secondary">Tenant ID Key</span>
          </div>
          <span className="text-xs text-semantic-text-secondary">Expires in <span className="font-semibold text-orange-500">{formatTime(expiresIn)}</span></span>
        </div>
        <div className="text-semantic-text-secondary text-sm flex-1">
          Generate a unique Tenant ID Key that you can use to deploy PAYG instances. This key will be associated with your account for billing purposes and is valid for 5 minutes.
        </div>
        {error && (
          <div className="text-red-500 text-sm w-full">{error}</div>
        )}
        <div className="w-full flex flex-col gap-2 mt-4">
          <div className="flex items-center gap-2">
            <input
              className="flex-1 bg-semantic-bg-primary border border-semantic-border-secondary rounded px-2 py-1 text-semantic-text-primary font-mono text-sm"
              value={tokenData?.token || ''}
              readOnly
              style={{ minWidth: 0 }}
            />
            {showCopiedTooltip && (
              <GenericTooltip 
                text="Copied to clipboard!"
                position="top"
                customStyle={{ width: "135px", maxWidth: "200px" }}
              >
                <GenericButton
                  variant="secondary"
                  size="sm"
                  iconLeftName="copy"
                  label=""
                  isIconOnly
                  icon={<IconCopy className="w-4 h-4 text-semantic-text-secondary" />}
                  onClick={handleCopy}
                  title="Copy token"
                />
              </GenericTooltip>
            )}
            {!showCopiedTooltip && (
              <GenericButton
                variant="secondary"
                size="sm"
                iconLeftName="copy"
                label=""
                isIconOnly
                icon={<IconCopy className="w-4 h-4 text-semantic-text-secondary" />}
                onClick={handleCopy}
                title="Copy token"
              />
            )}
          </div>
          <div className="flex items-center justify-end w-full text-xs text-semantic-text-secondary mt-1">
            <GenericButton
              label="Regenerate Token"
              variant="brand"
              size="md"
              onClick={handleRegenerate}
              loading={isLoading}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// No outer margin: the inner card's own body padding is the single inset, so the content
// box lines up with the Paid Licenses tab instead of gaining a third one (CTX-244).
const PAYG: React.FC = () => (
  <div className="relative">
    <GenericCard isCollapsible={false} hasBorder={false} shadow="flat"  
    bodyContent={
      <>
      <div className="flex flex-col ">
        <h1 className="text-2xl font-bold mb-1">Pay As You Go (PAYG)</h1>
        <p className="text-semantic-text-secondary text-body-small  mb-4">Generate a Tenant ID Key for deploying PAYG instances.</p>
      </div>
        <TenantIdKeyAlert />
      </>
    }>
    </GenericCard>
  </div>
);

export default PAYG;